import React, { useState } from 'react';
import { AlertCircle, BookOpen, CreditCard, Edit3, FileText, Landmark, Layers, Plus, Search } from 'lucide-react';
import { DataTable, ColumnDef } from '../../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction, deleteAction } from '../../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailField,
  DetailFieldList,
  DetailHeaderButton,
  DetailIdentity,
  DetailNotes,
  DetailSection
} from '../../../../../components/shared/DetailPanel/DetailBlocks';
import { SidebarForm } from '../../../../../components/shared/SidebarForm/SidebarForm';
import Modal from '../../../../../components/shared/Modal/Modal';
import { useClickOutside, isDetailKeepZone } from '../../../../../hooks/useClickOutside';
import { useToast } from '../../../../../hooks/useToast';
import { usePaymentMethods } from '../../../../../hooks/usePaymentMethods';
import {
  PaymentMethodInput,
  createPaymentMethod,
  deletePaymentMethod,
  updatePaymentMethod
} from '../../../../../services/PaymentMethodsService';
import { PaymentMethod, nextCode } from './financeMasters';
import '../../shared-modules.css';
import './FinanceMasters.css';

type MethodForm = PaymentMethodInput;

const EMPTY_FORM: MethodForm = {
  name: '',
  type: 'Bancario',
  account: '572000',
  requiresIban: true,
  description: '',
  isActive: true
};

const METHOD_TYPES: { value: string; label: string }[] = [
  { value: 'Bancario', label: 'Bancario (Transferencia / OPI)' },
  { value: 'Adeudo Directo', label: 'Adeudo Directo (SEPA B2B / CORE)' },
  { value: 'Efecto', label: 'Efecto Comercial (Pagaré / Cheque)' },
  { value: 'Metálico', label: 'Metálico (Caja 570)' },
  { value: 'Electrónico', label: 'Electrónico (TPV / Tarjeta / Stripe)' }
];

const METHOD_ACCOUNTS: Record<string, string> = {
  '572000': 'Bancos c/c (WISE EUR)',
  '570000': 'Caja, euros',
  '431000': 'Efectos comerciales en cartera',
  '520000': 'Deudas C/P entidades crédito'
};

export const FinancePaymentMethodsView = () => {
  const { notify } = useToast();

  const { methods, loading, error: loadError, reload } = usePaymentMethods();
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PaymentMethod | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<MethodForm>(EMPTY_FORM);
  const [formError, setFormError] = useState('');

  const setField = <K extends keyof MethodForm>(field: K, value: MethodForm[K]) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const handleCreateNewClick = () => {
    setSelectedMethod(null);
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleEditClick = (method: PaymentMethod) => {
    const { id, dbId, ...rest } = method;
    setSelectedMethod(method);
    setFormData({ ...EMPTY_FORM, ...rest });
    setIsEditing(true);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleSelectMethod = (method: PaymentMethod) => {
    setIsFormOpen(false);
    setSelectedMethod(method);
  };

  const closePanel = () => {
    setIsFormOpen(false);
    setSelectedMethod(null);
  };

  // Clic fuera cierra la ficha, el formulario no
  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedMethod) && !isFormOpen);

  const handleSaveMethod = async () => {
    if (saving) return;
    const name = formData.name.trim();
    if (!name) {
      setFormError('La denominación de la forma de pago es obligatoria.');
      return;
    }
    if (methods.some(m => m.name.toLowerCase() === name.toLowerCase() && m.id !== selectedMethod?.id)) {
      setFormError(`Ya existe una forma de pago llamada "${name}".`);
      return;
    }
    const data: MethodForm = { ...formData, name, description: formData.description.trim() };

    setSaving(true);
    setFormError('');
    try {
      let saved: PaymentMethod;
      if (isEditing && selectedMethod) {
        saved = await updatePaymentMethod(selectedMethod.dbId || '', data);
        notify('Éxito', `Forma de pago "${saved.name}" actualizada.`, 'info');
      } else {
        saved = await createPaymentMethod(nextCode('MET', methods.map(m => m.id)), data);
        notify('Éxito', `Forma de pago "${saved.name}" registrada.`, 'info');
      }
      await reload(true);
      setSelectedMethod(saved);
      setIsFormOpen(false);
    } catch (err) {
      setFormError((err as Error)?.message || 'No se pudo guardar la forma de pago.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await deletePaymentMethod(target.dbId || '');
      notify('Éxito', `Forma de pago "${target.name}" eliminada.`, 'info');
      if (selectedMethod?.id === target.id) closePanel();
      await reload(true);
    } catch (err) {
      notify('Formas de pago', (err as Error)?.message || 'Error al eliminar la forma de pago.', 'warning');
    }
  };

  const query = searchQuery.toLowerCase();
  const filteredMethods = methods.filter(m =>
    m.name.toLowerCase().includes(query) ||
    m.type.toLowerCase().includes(query) ||
    m.account.includes(query) ||
    m.id.toLowerCase().includes(query)
  );

  const columns: ColumnDef<PaymentMethod>[] = [
    {
      id: 'id',
      header: 'CÓDIGO',
      render: (m) => <span className="fin-master__code">{m.id}</span>
    },
    {
      id: 'name',
      header: 'DENOMINACIÓN',
      render: (m) => (
        <>
          <div className="fin-master__name">{m.name}</div>
          <div className="fin-master__desc">{m.description}</div>
        </>
      )
    },
    {
      id: 'type',
      header: 'TIPO DE MEDIO',
      render: (m) => <span className="fin-master__text">{m.type}</span>
    },
    {
      id: 'account',
      header: 'CUENTA PGC',
      render: (m) => <span className="fin-master__chip">{m.account}</span>
    },
    {
      id: 'requiresIban',
      header: 'REQUIERE IBAN',
      align: 'center',
      render: (m) => m.requiresIban
        ? <span className="module-badge active">Sí</span>
        : <span className="fin-master__muted">No</span>
    },
    {
      id: 'isActive',
      header: 'ESTADO',
      align: 'center',
      render: (m) => <span className={`module-badge ${m.isActive ? 'active' : 'inactive'}`}>{m.isActive ? 'Activa' : 'Inactiva'}</span>
    }
  ];

  return (
    <div className="fin-master">
      <div className="module-toolbar">
        <div className="module-search-wrapper">
          <Search className="module-search-icon" size={14} />
          <input
            type="text"
            placeholder="BUSCAR POR DENOMINACIÓN, TIPO DE INSTRUMENTO O CUENTA PGC..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="module-search-input"
          />
        </div>
        <button onClick={handleCreateNewClick} className="module-create-btn" data-keep-detail>
          <Plus size={14} /> Nueva_Forma_De_Pago
        </button>
      </div>

      <MasterDetailLayout isOpen={Boolean(selectedMethod || isFormOpen)}>
        <DataTable
          data={filteredMethods}
          columns={columns}
          title={<><CreditCard size={14} className="module-panel-title-icon" /> Maestro_De_Formas_De_Pago</>}
          subtitle={`TOTAL: ${filteredMethods.length}`}
          getRowId={(m) => m.id}
          onRowClick={handleSelectMethod}
          selectedRowId={selectedMethod?.id}
          emptyText={loading ? 'Cargando formas de pago...' : loadError || 'No se encontraron formas de pago.'}
          actionsHeader="ACCIONES"
          actionsColumn={(m) => (
            <RowActionsMenu
              actions={[
                viewAction(() => handleSelectMethod(m)),
                editAction(() => handleEditClick(m)),
                deleteAction(() => setDeleteTarget(m))
              ]}
            />
          )}
        />

        {isFormOpen ? (
          <SidebarForm
            expandable
            title={isEditing ? 'Modificar_Forma_De_Pago' : 'Alta_De_Forma_De_Pago'}
            onClose={closePanel}
            submitLabel={saving ? 'Guardando...' : 'Guardar_Forma_De_Pago'}
            onSubmit={handleSaveMethod}
          >
            {formError && (
              <div className="module-form-error">
                <AlertCircle size={14} style={{ flexShrink: 0 }} />
                <span>{formError}</span>
              </div>
            )}

            <DetailSection title="1. Datos_De_La_Forma_De_Pago" icon={FileText}>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Denominación *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="Ej. Transferencia SEPA Inmediata"
                  className="sidebar-form-input"
                />
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Tipo de Medio</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setField('type', e.target.value)}
                    className="sidebar-form-input"
                  >
                    {METHOD_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Cuenta Contable PGC</label>
                  <select
                    value={formData.account}
                    onChange={(e) => setField('account', e.target.value)}
                    className="sidebar-form-input"
                  >
                    {Object.keys(METHOD_ACCOUNTS).map(code => (
                      <option key={code} value={code}>{code} - {METHOD_ACCOUNTS[code]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Estado</label>
                <select
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setField('isActive', e.target.value === 'active')}
                  className="sidebar-form-input"
                >
                  <option value="active">ACTIVA</option>
                  <option value="inactive">INACTIVA</option>
                </select>
              </div>
              <label className="fin-master__check">
                <input
                  type="checkbox"
                  checked={formData.requiresIban}
                  onChange={(e) => setField('requiresIban', e.target.checked)}
                />
                Requiere IBAN / cuenta bancaria del cliente o proveedor
              </label>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Descripción</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setField('description', e.target.value)}
                  placeholder="Detalles sobre pasarela, remesa bancaria Norma 19 / 34..."
                  className="sidebar-form-input"
                />
              </div>
            </DetailSection>
          </SidebarForm>
        ) : selectedMethod ? (
          <DetailPanel
            expandable
            title="Ficha_De_Forma_De_Pago"
            onClose={closePanel}
            headerActions={
              <DetailHeaderButton icon={Edit3} label="Editar" onClick={() => handleEditClick(selectedMethod)} />
            }
          >
            <DetailIdentity
              icon={CreditCard}
              reference={selectedMethod.id}
              name={selectedMethod.name}
              subtitle={
                <span className={`module-badge ${selectedMethod.isActive ? 'active' : 'inactive'}`}>
                  {selectedMethod.isActive ? 'Activa' : 'Inactiva'}
                </span>
              }
            />

            <DetailFieldList>
              <DetailField icon={Layers} label="TIPO_DE_MEDIO">{selectedMethod.type}</DetailField>
              <DetailField icon={BookOpen} label="CUENTA_PGC">
                {`${selectedMethod.account} - ${METHOD_ACCOUNTS[selectedMethod.account] || ''}`}
              </DetailField>
              <DetailField icon={Landmark} label="REQUIERE_IBAN">{selectedMethod.requiresIban ? 'Sí' : 'No'}</DetailField>
            </DetailFieldList>

            {selectedMethod.description && <DetailNotes title="Descripción">{selectedMethod.description}</DetailNotes>}
          </DetailPanel>
        ) : null}
      </MasterDetailLayout>

      <Modal
        isOpen={!!deleteTarget}
        type="urgent"
        title="Eliminar forma de pago"
        message={`¿Eliminar "${deleteTarget?.name}"? Los clientes o proveedores que la tengan asignada conservarán el nombre, pero dejará de estar en el maestro. Para retirarla sin borrarla, márquela como inactiva.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default FinancePaymentMethodsView;
